import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type AIMode = "title" | "agenda" | "join_context" | "summarize";

export interface SummarizeResult {
  summary: string;
  key_points: string[];
  action_items: { task: string; owner: string }[];
}

export type AIErrorKind = "rate_limit" | "credits" | "network" | "unknown";

export interface AIError {
  kind: AIErrorKind;
  message: string;
}

const MODE_LABELS: Record<AIMode, string> = {
  title: "Generating title",
  agenda: "Building agenda",
  join_context: "Preparing context",
  summarize: "Summarizing meeting",
};

export function useAIAssist() {
  const [loading, setLoading] = useState<AIMode | null>(null);
  const [lastError, setLastError] = useState<AIError | null>(null);
  const toastIdRef = useRef<string | number | null>(null);

  const call = useCallback(async <T = any,>(payload: {
    mode: AIMode;
    intent?: string;
    context?: string;
    meetingId?: string;
    source?: "chat" | "manual" | "transcript";
    manualNotes?: string;
    label?: string;
  }): Promise<T | null> => {
    try {
      setLoading(payload.mode);
      setLastError(null);
      const label = payload.label || MODE_LABELS[payload.mode];
      toastIdRef.current = toast.loading(`AI: ${label}…`);
      const { data, error } = await supabase.functions.invoke("meeting-ai-assist", {
        body: payload,
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      if (toastIdRef.current) toast.dismiss(toastIdRef.current);
      return data as T;
    } catch (e: any) {
      const msg = e?.message || "AI assist failed";
      const lower = msg.toLowerCase();
      let kind: AIErrorKind = "unknown";
      if (lower.includes("rate limit") || lower.includes("429")) kind = "rate_limit";
      else if (lower.includes("credits") || lower.includes("402")) kind = "credits";
      else if (lower.includes("network") || lower.includes("fetch")) kind = "network";

      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      const errMsg =
        kind === "rate_limit"
          ? "AI is busy — please try again in a moment."
          : kind === "credits"
            ? "AI credits exhausted. Add credits in workspace settings."
            : kind === "network"
              ? "Network issue — check your connection."
              : msg;
      toast.error(errMsg);
      setLastError({ kind, message: msg });
      return null;
    } finally {
      setLoading(null);
      toastIdRef.current = null;
    }
  }, []);

  return { call, loading, lastError, clearError: () => setLastError(null) };
}
